/* eslint-disable react/jsx-no-bind */
import React, { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { APIProvider, Map, RenderingType, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { desktopMinWidth, getMapOptions, mapConfig } from '@constants/component-configs';
import { key as googleMapsApiKey, mapId } from '@constants/google-maps';

const libraries = ['geometry', 'places', 'marker'];  // For performance, keep 'libraries' outside the component,
                                          // to avoid unintentional reloads.

// google.maps.TrafficLayer isn't wrapped by @vis.gl/react-google-maps, but it's unaffected by the
// AdvancedMarker deprecation, so it's attached directly via the native class.
const TrafficLayer = ({ active }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !active) {
      return void 0; // eslint-disable-line no-void
    }
    const layer = new window.google.maps.TrafficLayer();
    layer.setMap(map);
    return () => layer.setMap(null);
  }, [active, map]);

  return null;
};

TrafficLayer.propTypes = {
  active: PropTypes.bool
};

const MapLoadListener = ({ onMapLoad }) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      onMapLoad?.(map);
    }
  }, [map, onMapLoad]);

  return null;
};

MapLoadListener.propTypes = {
  onMapLoad: PropTypes.func
};

// Pans/zooms the map imperatively when `focusRequest` changes (search result selected, "use my
// location" clicked - see setFocusRequest() in map-actions.js). center/zoom on <Map> are
// intentionally uncontrolled (defaultCenter/defaultZoom below) so free user panning/zooming isn't
// fought by re-renders driven by the constantly-updating viewport.center/zoom in Redux (which is
// also written on every onIdle) - this listener is the one legitimate case that still needs to
// move the map programmatically. requestId (not center/zoom) is the effect's dependency, so
// re-requesting the same spot still re-triggers it.
const MapFocusListener = ({ focusRequest }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !focusRequest) {
      return;
    }
    map.panTo(focusRequest.center);
    if (focusRequest.zoom) {
      map.setZoom(focusRequest.zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusRequest?.requestId]);

  return null;
};

MapFocusListener.propTypes = {
  focusRequest: PropTypes.object
};

// This app opens the entity tray purely via the map's own global 'click' event -> onSelectArea()
// -> a radius search around the click point (no shape/marker ever gets its own onClick handler -
// see map-figure.js/line-segment.js/point.js). But Google's map-level 'click' event doesn't fire
// when the click lands on a clickable overlay (lines default to clickable:true) - the overlay
// absorbs it - so clicking directly on a line silently did nothing. Same root cause as the
// mousemove bug fixed in MouseTracker below, applied to clicks: bypass the Maps API event, listen
// for native click on the map's own div in the capture phase (runs before any overlay can stop
// propagation), and convert pixel -> LatLng via a persistent, invisible OverlayView projection.
// Google's own UI controls (zoom, street view pegman, fullscreen...) render as <button> elements
// inside this same container - skip those so their clicks aren't hijacked into an area search.
const ClickTracker = ({ onClick }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !onClick) {
      return void 0; // eslint-disable-line no-void
    }
    const projectionOverlay = new window.google.maps.OverlayView();
    projectionOverlay.onAdd = () => {};
    projectionOverlay.draw = () => {};
    projectionOverlay.onRemove = () => {};
    projectionOverlay.setMap(map);

    const container = map.getDiv();
    const handleClick = domEvent => {
      if (domEvent.target.closest('button, [role="button"]')) {
        return;
      }
      const projection = projectionOverlay.getProjection();
      if (!projection) {
        return;
      }
      const rect = container.getBoundingClientRect();
      const point = new window.google.maps.Point(domEvent.clientX - rect.left, domEvent.clientY - rect.top);
      const latLng = projection.fromContainerPixelToLatLng(point);
      if (latLng) {
        onClick({ latLng, domEvent });
      }
    };
    container.addEventListener('click', handleClick, true);

    return () => {
      container.removeEventListener('click', handleClick, true);
      projectionOverlay.setMap(null);
    };
  }, [map, onClick]);

  return null;
};

ClickTracker.propTypes = {
  onClick: PropTypes.func
};

const containerStyle = { width: '100%', height: '100%' };

const DotMapsGoogleMapInner = ({
  center,
  components,
  focusRequest,
  mapTypeId,
  onBoundsChanged,
  onClick,
  onIdle,
  onMapLoad,
  traffic,
  zoom
}) => {
  const isLoaded = useApiIsLoaded();
  if (!isLoaded) {
    return null;
  }

  const mapOptions = window.screen.width >= desktopMinWidth
    ? {...getMapOptions(window.google).mapOptions}
    : {...mapConfig.mobileMapOptions};

  return (
    <Map
      {...mapOptions}
      key={mapId}
      defaultCenter={center}
      defaultZoom={zoom}
      mapId={mapId}
      mapTypeId={mapTypeId}
      onBoundsChanged={onBoundsChanged}
      onIdle={onIdle}
      renderingType={RenderingType.VECTOR}
      reuseMaps
      style={containerStyle}
    >
      <MapLoadListener onMapLoad={onMapLoad} />
      <MapFocusListener focusRequest={focusRequest} />
      <ClickTracker onClick={onClick} />
      <TrafficLayer active={traffic} />
      {components}
    </Map>
  );
};

DotMapsGoogleMapInner.propTypes = {
  center: PropTypes.object,
  components: PropTypes.node,
  focusRequest: PropTypes.object,
  mapTypeId: PropTypes.string,
  onBoundsChanged: PropTypes.func,
  onClick: PropTypes.func,
  onIdle: PropTypes.func,
  onMapLoad: PropTypes.func,
  traffic: PropTypes.bool,
  zoom: PropTypes.number
};

const DotMapsGoogleMap = props => (
  <APIProvider apiKey={googleMapsApiKey} libraries={libraries}>
    <DotMapsGoogleMapInner {...props} />
  </APIProvider>
);

export default memo(DotMapsGoogleMap);
